import React, { useEffect, useState } from "react";

import styles from "./LoginPage.module.scss";
import { SubmitHandler, useForm } from "react-hook-form";
import { POST } from "../../services/axios.service";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { route } from "../../static/route";
import { useToastHandler } from "../../hooks/useToastHandler";
import { t } from "../../locales";
import { useAppDispatch } from "../../app/hooks";
import { setBranchesFromResponse } from "../../features/settings/settingsSlice";
import { setPermissionsFromLogin } from "../../features/loggingUser/loggingUserSlice";

const LoginPage = () => {
  const companyName = process.env.REACT_APP_COMPANY_NAME;

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isAuthenticated, setIsAuthenticated } = useAuth();
  const { handleError, handleSuccess } = useToastHandler();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);

  interface IFormInput {
    phone: string;
    password: string;
  }

  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<IFormInput>();

  const handleOnSubmit: SubmitHandler<IFormInput> = async (data: any) => {
    setIsLoading(true);

    await POST("/v1/login", {
      phone: data.phone,
      password: data.password,
    })
      .then((response: any) => {
        if (response.data.status === "success") {
          handleSuccess(response);
          localStorage.setItem("authToken", response.data.data.token);
          localStorage.removeItem("selectedBranch"); // clear stale branch before evaluating
          dispatch(setBranchesFromResponse(response.data.data.branches ?? []));
          dispatch(setPermissionsFromLogin({
            permissions: response.data.data.permissions ?? {},
            roles: response.data.data.roles ?? {},
          }));
          setIsAuthenticated(true);
          navigate(route.dashboard);
        } else {
          handleError(response);
        }
      })
      .catch((error: any) => {
        handleError(error);
      });

    setIsLoading(false);
  };

  useEffect(() => {
    if (isAuthenticated) navigate(route.dashboard);
  }, [isAuthenticated]);

  // Clear service worker caches on login page load
  useEffect(() => {
    caches.keys().then((names) => {
      names.forEach((name) => {
        caches.delete(name);
      });
    });
  }, []);

  return (
    <div className={styles.LoginPage}>

      {/* LEFT PANEL */}
      <div className="relative w-1/2 min-h-full bg-[linear-gradient(135deg,#113F67_0%,#1a4f7a_50%,#34699A_100%)] flex items-center justify-center overflow-hidden max-lg:hidden">
        <div className="absolute top-20 left-20 w-32 h-32 bg-white/10 rounded-full blur-xl pointer-events-none" />
        <div className="absolute bottom-32 right-16 w-24 h-24 bg-white/[0.05] rounded-full blur-lg pointer-events-none" />
        <div className="absolute top-1/2 left-10 w-16 h-16 bg-white/[0.05] rounded-full blur-md pointer-events-none" />
        <div className="relative z-[2] px-[52px] w-full box-border animate-fade-up-slow max-lg:animate-none">

          {/* Brand row */}
          <div className="flex items-center gap-4 mb-1">
            <div className="w-12 h-12 shrink-0 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center text-white">
              {/* Package / box icon */}
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
                <line x1="12" y1="22.08" x2="12" y2="12"/>
              </svg>
            </div>
            <div>
              <h1 className="text-5xl font-bold text-white m-0 leading-[1.2] tracking-[-0.3px]">{companyName}</h1>
              <p className="text-xl text-white/[0.55] mt-0.5">{t.login.erpSystem}</p>
            </div>
          </div>

          <hr className="border-0 border-t border-white/20 w-20 my-[22px]" />

          <p className="text-xl text-white/[0.65] leading-[1.65] mb-8 max-w-[300px]">
            {t.login.tagline}
          </p>

          {/* Feature items */}
          <div className="flex flex-col">
            <div className="flex items-start gap-[14px] mb-5">
              <div className="w-10 h-10 shrink-0 rounded-[10px] bg-white/[0.08] border border-white/[0.18] flex items-center justify-center text-white/[0.85] mt-0.5">
                {/* Shield icon */}
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                </svg>
              </div>
              <div>
                <p className="text-sm font-semibold text-white m-0 mb-[3px]">{t.login.feature1Title}</p>
                <p className="text-sm text-white/50 m-0">{t.login.feature1Desc}</p>
              </div>
            </div>

            <div className="flex items-start gap-[14px] mb-5">
              <div className="w-10 h-10 shrink-0 rounded-[10px] bg-white/[0.08] border border-white/[0.18] flex items-center justify-center text-white/[0.85] mt-0.5">
                {/* Trending chart icon */}
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
                  <polyline points="17 6 23 6 23 12"/>
                </svg>
              </div>
              <div>
                <p className="text-sm font-semibold text-white m-0 mb-[3px]">{t.login.feature2Title}</p>
                <p className="text-sm text-white/50 m-0">{t.login.feature2Desc}</p>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* RIGHT PANEL */}
      <div className="flex-1 bg-[linear-gradient(135deg,#f8fafc_0%,#eff6ff_50%,#eef2ff_100%)] flex flex-col items-center justify-center py-10 px-6 overflow-y-auto max-lg:w-full max-lg:px-5 max-lg:pt-10 max-lg:pb-12">

        {/* Mobile brand header — hidden on desktop */}
        <div className="hidden max-lg:flex items-center justify-center gap-[14px] mb-6 w-full max-w-[440px]">
          <div className="w-12 h-12 shrink-0 rounded-xl bg-[linear-gradient(135deg,#113F67,#34699A)] flex items-center justify-center text-white">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
              <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
              <line x1="12" y1="22.08" x2="12" y2="12"/>
            </svg>
          </div>
          <div>
            <h1 className="text-2xl font-bold text-brand-dark m-0 mb-0.5 leading-[1.2]">{companyName}</h1>
            <p className="text-sm text-gray-500 m-0">{t.login.erpSystem}</p>
          </div>
        </div>

        <div className="bg-white/80 backdrop-blur-sm rounded-2xl py-10 px-9 shadow-card w-full max-w-[440px] animate-fade-up-card max-lg:py-7 max-lg:px-6 max-lg:animate-none max-lg:shadow-card-mobile">
          <h2 className="text-3xl font-bold text-brand-dark m-0 mb-1.5 text-center tracking-[-0.4px] max-lg:text-[1.55rem]">{t.login.title}</h2>
          <p className="text-base text-gray-500 m-0 mb-7 text-center">{t.login.subtitle}</p>

          <form onSubmit={handleSubmit(handleOnSubmit)}>

            {/* Mobile Number */}
            <div className="flex flex-col mb-5">
              <label className="text-sm font-semibold text-brand-dark mb-[7px]">{t.login.mobileLabel}</label>
              <div className="relative flex items-center">
                <span className="absolute left-[14px] top-1/2 -translate-y-1/2 text-brand-light flex items-center pointer-events-none">
                  {/* Phone icon */}
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.62 1h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 8.91a16 16 0 0 0 6 6l.72-.87a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                  </svg>
                </span>
                <input
                  className={`w-full h-14 pl-12 pr-4 text-base text-brand-dark bg-white border-2 rounded-xl outline-none box-border transition-[border-color,box-shadow] duration-200 placeholder:text-gray-400 focus:border-brand-dark focus:shadow-focus-brand ${errors.phone ? 'border-red-400 shadow-focus-error' : 'border-gray-200'}`}
                  type="text"
                  placeholder={t.login.mobilePlaceholder}
                  {...register("phone", { required: true })}
                />
                {errors.phone && (
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-red-500 flex items-center pointer-events-none">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"/>
                      <line x1="12" y1="8" x2="12" y2="12"/>
                      <line x1="12" y1="16" x2="12.01" y2="16"/>
                    </svg>
                  </span>
                )}
              </div>
              {errors.phone && errors.phone.type === "required" && (
                <span className="text-sm text-red-500 mt-1 flex items-center gap-1">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="12" y1="8" x2="12" y2="12"/>
                    <line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  {t.login.mobileRequired}
                </span>
              )}
            </div>

            {/* Password */}
            <div className="flex flex-col mb-5">
              <label className="text-sm font-semibold text-brand-dark mb-[7px]">{t.login.passwordLabel}</label>
              <div className="relative flex items-center">
                <span className="absolute left-[14px] top-1/2 -translate-y-1/2 text-brand-light flex items-center pointer-events-none">
                  {/* Lock icon */}
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                  </svg>
                </span>
                <input
                  className={`w-full h-14 pl-12 pr-12 text-base text-brand-dark bg-white border-2 rounded-xl outline-none box-border transition-[border-color,box-shadow] duration-200 placeholder:text-gray-400 focus:border-brand-dark focus:shadow-focus-brand ${errors.password ? 'border-red-400 shadow-focus-error' : 'border-gray-200'}`}
                  type={showPassword ? "text" : "password"}
                  placeholder={t.login.passwordPlaceholder}
                  {...register("password", { required: true })}
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 bg-transparent border-none cursor-pointer p-1 flex items-center text-brand-light transition-colors duration-150 hover:text-brand-dark focus:outline-none"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    /* Eye-off icon */
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </svg>
                  ) : (
                    /* Eye icon */
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  )}
                </button>
              </div>
              {errors.password && errors.password.type === "required" && (
                <span className="text-sm text-red-500 mt-1 flex items-center gap-1">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="12" y1="8" x2="12" y2="12"/>
                    <line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  {t.login.passwordRequired}
                </span>
              )}
            </div>

            {/* Forgot password */}
            <div className="text-right -mt-2 mb-[18px]">
              <div className="relative inline-block group">
                <a href="#" className="text-sm text-brand-light no-underline font-medium hover:text-brand-dark hover:underline">
                  {t.login.forgotPassword}
                </a>
                {/* Tooltip */}
                <div className="absolute bottom-full right-0 mb-2 hidden group-hover:block z-10 pointer-events-none">
                  <div className="bg-gray-800 text-white text-xs rounded py-1.5 px-3 whitespace-nowrap shadow-lg">
                    {t.login.forgotPasswordTooltip}
                    {/* Arrow */}
                    <div className="absolute top-full right-3 border-4 border-transparent border-t-gray-800"></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Submit */}
            <button
              className="w-full py-3.5 px-6 text-lg font-semibold text-white bg-[linear-gradient(to_right,#113F67,#34699A)] border-none rounded-[10px] cursor-pointer tracking-[0.02em] transition-[background,transform,box-shadow] duration-200 disabled:opacity-60 disabled:cursor-not-allowed hover:enabled:bg-[linear-gradient(to_right,#0d2f4d,#2a547b)] hover:enabled:scale-[1.02] hover:enabled:shadow-btn-hover active:enabled:translate-y-px active:enabled:shadow-none"
              type="submit"
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="flex items-center gap-2 justify-center">
                  <span className="w-5 h-5 rounded-full border-2 border-white/30 border-t-white animate-spin shrink-0"></span>
                  {t.login.loggingIn}
                </div>
              ) : t.login.loginButton}
            </button>
          </form>
        </div>

        {/* Copyright */}
        <p className="text-sm text-gray-400 mt-6 text-center">© 2026 {companyName}. {t.login.copyright}</p>
      </div>

    </div>
  );
};

export default LoginPage;
